import {
  resetNavigation,
  setDefaultSettings,
  setInitialSettings,
} from 'app/store/actions/fuse'
import _ from '@lodash'
import { compose, set, lensProp, pick } from 'ramda'
import { refreshTokenWithProfile, loginWithSSO } from 'app/auth/authAPI'
import {
  updateAccessToken,
  updateMaModeKey,
  removeToken,
  saveToken,
} from 'app/services/userService/userStorage'
import { setPlace } from 'app/store/actions/place.actions'
import { getUserAvatar, getDisplayName } from '../reducers/user.query'
import { spacialCharDecode } from 'commons/specialCharacter'
import { teardownSocketConnection } from 'app/main/messages/conversations/socket'
import effects from 'app/effects/effects'
import { clearGroups } from 'app/store/groups'
import { resetCSVFileData } from 'app/store/importCSV'
import { hideImportProcessBarAndDisconnectSocket } from 'app/main/subscribersV4/AddSubscibers/components/newImport/BackgroundProgressBarImportProcess/helpers'

export const SET_USER_DATA = '[USER] SET DATA'
export const REMOVE_USER_DATA = '[USER] REMOVE DATA'
export const USER_LOGGED_OUT = '[USER] LOGGED OUT'
export const SET_USER_TOKEN = '[USER] SET TOKEN'
export const UPDATE_USER_ACCESS_TOKEN = '[USER] UPDATE ACCESS TOKEN'
export const UPDATE_INTERESTED_BEACON = '[USER] SET UPDATE INTERESTED BEACON'
export const UPDATE_USER_DATA = '[USER] SET UPDATE USER PROFILE'
export const UPDATE_USER_ROLES = '[USER] UPDATE ROLES'

export function userToUserData(user, token, place, awsCredential) {
  return {
    data: {
      ...user,
      displayName: getDisplayName(user),
      photoURL: getUserAvatar(user),
      settings: {},
    },
    place,
    token,
    awsCredential,
  }
}

export const setUserDataByUserAPI = compose(setUserData, userToUserData)

/**
 * Set User Data
 */
export function setUserData(user) {
  return (dispatch) => {
    /*
        Set User Settings
         */
    dispatch(setDefaultSettings(user.data.settings))
    dispatch(setPlace(user.place))

    /*
    Set User Data
    */
    saveToken(user.token)
    dispatch({
      type: SET_USER_DATA,
      payload: pick(['data', 'token', 'awsCredential'], user),
    })
  }
}

/**
 * Update User Settings
 */
export function updateUserSettings(settings) {
  return (dispatch, getState) => {
    const oldUser = getState().auth.user
    const user = _.merge({}, oldUser, { data: { settings } })

    setUserData(user)

    return dispatch(setUserData(user))
  }
}

/**
 * Remove User Data
 */
export function removeUserData() {
  return {
    type: REMOVE_USER_DATA,
  }
}

export const logoutFrontEnd = (dispatch) => {
  hideImportProcessBarAndDisconnectSocket(dispatch)
  removeToken()
  teardownSocketConnection()
  dispatch(setInitialSettings())
  dispatch({
    type: USER_LOGGED_OUT,
  })
  dispatch(resetNavigation())
}

/**
 * Logout
 */
export function logoutUser() {
  return (dispatch) => {
    effects.apis
      .logout()
      .then(() => {
        hideImportProcessBarAndDisconnectSocket(dispatch)
        removeToken()
        teardownSocketConnection()
        dispatch(setInitialSettings())
        dispatch({
          type: USER_LOGGED_OUT,
        })
        dispatch(clearGroups())
        dispatch(resetNavigation())
        dispatch(resetCSVFileData())
      })
      .catch((error) => {
        console.log('error:', error)
      })
  }
}

export function refreshTokenAction({ refreshToken, maModeKey }) {
  return (dispatch) =>
    refreshTokenWithProfile()
      .then(({ token, user, place, awsCredential }) => {
        updateAccessToken(token)
        return dispatch(
          setUserDataByUserAPI(
            user,
            set(lensProp('refreshToken'), refreshToken, {
              ...token,
              maModeKey,
            }),
            place,
            awsCredential,
          ),
        )
      })
      .catch((error) => {
        handleError(error, dispatch)
      })
}

export function loginWithSSOAction(token, maModeKey, dispatch) {
  const maModeKeyDecode = spacialCharDecode(maModeKey)
  updateMaModeKey(maModeKeyDecode)
  return loginWithSSO({ token })
    .then(({ token, user, place, awsCredential }) => {
      const mapMaKeyInToken = { ...token, maModeKey: maModeKeyDecode }
      updateAccessToken(mapMaKeyInToken)

      return dispatch(
        setUserDataByUserAPI(user, mapMaKeyInToken, place, awsCredential),
      )
    })
    .catch((error) => {
      handleError(error, dispatch)
    })
}

function handleError(error, dispatch) {
  if (
    (error && error.response && error.response.data.status !== 503) ||
    error.type === 'ERROR_SUSPEND'
  ) {
    return dispatch(logoutUser())
  } else if (
    error &&
    error.response.data.status === 503 &&
    error.response.data.details &&
    error.response.data.details.code === 700
  ) {
    dispatch({
      type: 'DISABLED_MAINTENANCE',
      value: true,
    })
  }
}

export function setUserToken({
  accessToken,
  refreshToken,
  expiresIn,
  expiresAt,
}) {
  return {
    type: SET_USER_TOKEN,
    token: {
      accessToken,
      refreshToken,
      expiresIn,
      expiresAt,
    },
  }
}

export function updateUserAccessToken({ accessToken, expiresIn, expiresAt }) {
  return {
    type: UPDATE_USER_ACCESS_TOKEN,
    token: {
      accessToken,
      expiresIn,
      expiresAt,
    },
  }
}

export function updateInterestedBeacon(value) {
  return {
    type: UPDATE_INTERESTED_BEACON,
    value: value,
  }
}

export function updateUserRoles(payload) {
  return {
    type: UPDATE_USER_ROLES,
    payload,
  }
}
